import { useEffect, useState } from "react";
import { Warning, CheckCircle, Clock, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";

/**
 * Ops-health card for the Mercury Reaper (background sweeper of stuck reservations).
 * Renders on /app/admin/metrics. Auto-refreshes every 60s.
 */
export default function MercuryReaperCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await client.get("/admin/mercury/reaper-health");
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load reaper health");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="mercury-reaper-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading Mercury Reaper health…</div>
      </div>
    );
  }
  if (!data) return null;

  const hasStuck = data.currently_reserving_over_10min > 0;
  const stateColor = hasStuck ? "text-amber-400" : "text-emerald-400";
  const stateIcon = hasStuck
    ? <Warning size={13} weight="fill" className="text-amber-400" />
    : <CheckCircle size={13} weight="fill" className="text-emerald-400" />;

  return (
    <div className="card-surface p-6" data-testid="mercury-reaper-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            <Clock size={13} />
            Mercury Reaper Health
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${stateColor}`} data-testid="reaper-state">
              {hasStuck ? "STUCK RESERVATIONS DETECTED" : "ALL CLEAR"}
            </span>
            {stateIcon}
          </div>
        </div>
        <button
          onClick={() => load()}
          className="text-neutral-500 hover:text-white transition-colors p-1"
          disabled={refreshing}
          data-testid="reaper-refresh-btn"
          title="Refresh"
        >
          <ArrowClockwise size={16} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <ReaperStat
          testid="reaper-reaped-24h"
          label="Reaped 24h"
          value={data.reaped_24h}
          hint="deleted since yesterday"
          tone="default"
        />
        <ReaperStat
          testid="reaper-stuck-over-10min"
          label="Stuck > 10min"
          value={data.currently_reserving_over_10min}
          hint="reserving longer than expected"
          tone={data.currently_reserving_over_10min > 0 ? "warn" : "good"}
        />
        <ReaperStat
          testid="reaper-currently-reserving"
          label="Reserving now"
          value={data.currently_reserving_total}
          hint="all in-flight reservations"
          tone="info"
        />
        <ReaperStat
          testid="reaper-oldest-age"
          label="Oldest age"
          value={data.oldest_reservation_age_min !== null ? `${data.oldest_reservation_age_min}m` : "—"}
          hint={data.oldest_reservation_dedupe_key ? `key: ${data.oldest_reservation_dedupe_key.slice(0, 12)}…` : "no active reservation"}
          tone="default"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-white/5 flex flex-wrap items-center gap-3 text-[11px] text-neutral-500">
        <span className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${data.reaper_running ? "bg-emerald-400" : "bg-red-500"}`} />
          Sweeper {data.reaper_running ? "running" : "stopped"}
        </span>
        <span>· Interval: every {data.reaper_interval_min}min</span>
        <span>· Stale threshold: {data.stale_threshold_min}min</span>
      </div>
    </div>
  );
}


function ReaperStat({ testid, label, value, hint, tone }) {
  const toneMap = {
    default: "text-white",
    good: "text-emerald-400",
    warn: "text-amber-400",
    info: "text-[#007AFF]",
  };
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`font-display text-2xl font-semibold tracking-tight ${toneMap[tone]}`}>{value}</div>
      <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{hint}</div>
    </div>
  );
}
